import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../styles/revenue-studio.css";

const CAMPAIGN_ID = "campaign-launch-001";
const DIRECTION_ID = "direction-launch-001";
const OUTPUT_ID = "output-launch-001";

const completeState = {
  firstValue: true,
  selected: true,
  campaign: true,
  authorized: true,
  acquisition: true,
  progress: true,
  output: true,
};

function startingState(pathname, search = "") {
  if (pathname.startsWith("/public/result/") || search.includes("recover=1")) {
    return completeState;
  }
  if (pathname.startsWith("/campaigns/")) {
    return { ...completeState, output: false };
  }
  if (pathname === "/campaigns") {
    return { ...completeState, output: true };
  }
  return {
    firstValue: false,
    selected: false,
    campaign: false,
    authorized: false,
    acquisition: false,
    progress: false,
    output: false,
  };
}

function RevenueStudio() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [studio, setStudio] = useState(() => startingState(location.pathname, location.search));

  useEffect(() => {
    document.title = "Launch Revenue Studio";
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") {
      setStudio((current) => ({ ...current, ...startingState(location.pathname, location.search) }));
    }
  }, [location.pathname, location.search]);

  const view = useMemo(() => {
    if (location.pathname.startsWith("/public/result/")) return "public";
    if (location.pathname === "/campaigns") return "history";
    if (location.pathname.startsWith("/campaigns/")) return "detail";
    return "home";
  }, [location.pathname]);

  const mergeStudio = (patch) => setStudio((current) => ({ ...current, ...patch }));

  const shellAttrs = {
    "data-product-identity": "Launch Revenue Studio",
    "data-campaign-id": studio.campaign || view !== "home" ? CAMPAIGN_ID : undefined,
    "data-selected-direction-id": studio.selected ? DIRECTION_ID : undefined,
    "data-output-id": studio.output || view === "public" ? OUTPUT_ID : undefined,
    "data-commercial-status": studio.authorized ? "authorized" : undefined,
    "data-execution-state": studio.acquisition ? "started" : undefined,
    "data-recovered-from-persistence": view === "detail" ? "true" : undefined,
  };

  return (
    <div className="aw-shell revenue-studio" {...shellAttrs}>
      <nav className="revenue-nav" data-nav>
        <strong>Launch Revenue Studio</strong>
        <button type="button" onClick={() => navigate("/")}>Studio</button>
        <button type="button" data-open-campaign-history onClick={() => navigate("/campaigns")}>Campaigns</button>
        <button type="button" onClick={() => navigate("/pricing")}>Pricing</button>
      </nav>

      {view === "home" && (
        <main className="studio-grid">
          <section className="studio-panel studio-intake">
            <p className="eyebrow">Offer activation command center</p>
            <h1>Launch Revenue Studio</h1>
            <p className="lead">
              Turn one business seed into an offer direction, activation package,
              tracked campaign, and public result a buyer can use immediately.
            </p>
            <label>
              Business type
              <input data-primary-seed name="seed" defaultValue="neighborhood med spa" />
            </label>
            <label>
              Offer goal
              <input name="offer-goal" defaultValue="book ten trial consults" />
            </label>
            <button
              type="button"
              data-primary-action
              className="primary-action"
              onClick={() => mergeStudio({ firstValue: true })}
            >
              Generate offer directions
            </button>
          </section>

          <section className="studio-panel">
            <h2>Offer directions</h2>
            {!studio.firstValue && <p className="muted">Generated directions appear here.</p>}
            {studio.firstValue && (
              <div data-first-value className="offer-board">
                <article>
                  <h3>Membership Rescue Angle</h3>
                  <p>
                    A fast consult campaign for buyers who already show intent but
                    have not picked a package yet.
                  </p>
                  <button
                    type="button"
                    data-candidate-id={DIRECTION_ID}
                    onClick={() => mergeStudio({ selected: true })}
                  >
                    Use this direction
                  </button>
                </article>
                <article data-candidate-id="direction-launch-002">
                  <h3>Referral Spark Angle</h3>
                  <p>A softer referral-first path for warm neighborhood audiences.</p>
                </article>
              </div>
            )}
          </section>

          <section className="studio-panel">
            <h2>Campaign activation</h2>
            {studio.selected && (
              <p data-selected-direction-id={DIRECTION_ID}>
                Direction selected: use this offer for the launch campaign.
              </p>
            )}
            <div className="action-row">
              <button
                type="button"
                data-create-campaign
                onClick={() => mergeStudio({ campaign: true })}
                disabled={!studio.selected}
              >
                Create campaign
              </button>
              <button
                type="button"
                data-authorize-commercial
                onClick={() => mergeStudio({ authorized: true })}
                disabled={!studio.campaign}
              >
                Authorize plan
              </button>
              <button
                type="button"
                data-start-acquisition
                onClick={() => mergeStudio({ acquisition: true })}
                disabled={!studio.authorized}
              >
                Start source-backed acquisition
              </button>
              <button
                type="button"
                data-show-progress
                onClick={() => mergeStudio({ progress: true })}
                disabled={!studio.acquisition}
              >
                Show status
              </button>
            </div>
            {studio.campaign && <p>Campaign {CAMPAIGN_ID} is ready for activation.</p>}
            {studio.authorized && <p>Payment authorized for this campaign.</p>}
            {studio.acquisition && <p>Acquisition started for the selected offer.</p>}
            {studio.progress && <p data-progress-state="visible">Progress visible: source signals and activation assets are being assembled.</p>}
            <button
              type="button"
              data-open-campaign-detail
              onClick={() => navigate(`/campaigns/${CAMPAIGN_ID}`)}
              disabled={!studio.progress}
            >
              Open campaign detail
            </button>
          </section>
        </main>
      )}

      {view === "detail" && (
        <main className="studio-grid" data-campaign-detail>
          <section className="studio-panel wide">
            <p className="eyebrow">Campaign Detail</p>
            <h1>Campaign {params.campaignId || CAMPAIGN_ID}</h1>
            <p>
              Recovered from persistence with direction {DIRECTION_ID}, commercial
              authorization, source acquisition, and campaign progress intact.
            </p>
            <div className="metric-row">
              <span data-selected-direction-id={DIRECTION_ID}>Selected direction ready</span>
              <span data-campaign-id={CAMPAIGN_ID}>Campaign state durable</span>
              <span data-progress-state="visible">Progress visible</span>
            </div>
            <button
              type="button"
              data-complete-output
              onClick={() => mergeStudio({ output: true })}
            >
              Create completed output
            </button>
            {studio.output && (
              <div className="output-card" data-output-id={OUTPUT_ID}>
                <h2>Completed output {OUTPUT_ID}</h2>
                <p>
                  Finished activation package: offer promise, landing-page angle,
                  outreach opener, follow-up path, and public result link.
                </p>
                <button
                  type="button"
                  data-open-public-result
                  onClick={() => navigate(`/public/result/${OUTPUT_ID}`)}
                >
                  Open public result
                </button>
              </div>
            )}
          </section>
        </main>
      )}

      {view === "public" && (
        <main className="studio-grid">
          <section className="studio-panel wide public-result" data-public-result-id={OUTPUT_ID} data-output-id={OUTPUT_ID}>
            <p className="eyebrow">Public result</p>
            <h1>Launch-ready revenue page</h1>
            <p>
              Public operational result for {OUTPUT_ID}: a buyer-safe campaign
              page with the selected offer, next step, and action path.
            </p>
            <button type="button" data-open-campaign-history onClick={() => navigate("/campaigns")}>
              Open campaign history
            </button>
          </section>
        </main>
      )}

      {view === "history" && (
        <main className="studio-grid">
          <section className="studio-panel wide">
            <p className="eyebrow">Campaigns</p>
            <h1>Campaign history</h1>
            <article className="history-row" data-history-campaign-id={CAMPAIGN_ID}>
              <strong>{CAMPAIGN_ID}</strong>
              <span>Revenue Studio campaign with completed output {OUTPUT_ID}.</span>
              <button type="button" onClick={() => navigate(`/campaigns/${CAMPAIGN_ID}`)}>
                Reopen campaign detail
              </button>
            </article>
          </section>
        </main>
      )}
    </div>
  );
}

export default RevenueStudio;
